import { request, requestPage } from '@/lib/apiClient';
import { endpoints } from '@/features/endpoints';
import type { CoachApplicationDetail, CoachApplicationSummary } from '@/types/domain';
import type { Page } from '@/types/api';

/**
 * `GET /admin/trainers/pending` - oldest first.
 *
 * FIFO is a product requirement, not a default: reviewers naturally pick the
 * easy-looking applications, and without a forced order the awkward ones sit at
 * the bottom of the queue for weeks. The client does not offer a sort control
 * for this reason.
 */
export function fetchPendingApplications(
  params: { limit?: number; cursor?: string; q?: string },
  signal?: AbortSignal,
): Promise<Page<CoachApplicationSummary>> {
  return requestPage<CoachApplicationSummary>(endpoints.trainers.pending, {
    query: {
      limit: params.limit ?? 20,
      cursor: params.cursor,
      q: params.q,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchApplication(
  id: string,
  signal?: AbortSignal,
): Promise<CoachApplicationDetail> {
  return request<CoachApplicationDetail>(endpoints.trainers.detail(id), signal ? { signal } : {});
}

export type ApprovalDecision =
  | {
      approval: 'approved';
      /**
       * The coach's STARTING rating, 0-5 in half steps.
       *
       * Optional, and leaving it out is a real choice: a coach approved with no
       * seed starts at 0 and shows as "New", which is the honest state. It
       * never comes with a review count - the platform does not invent reviews
       * to back an editorial number.
       */
      initialRating?: number;
      /** Why that number. Recorded for staff; members never see it. */
      initialRatingNote?: string;
    }
  | { approval: 'rejected'; rejectionReason: string };

/**
 * `PATCH /admin/trainers/:id/approval`.
 *
 * ⚠ Approving publishes this coach to every member in the discovery feed. The
 * UI requires an explicit confirmation naming the coach before this is called.
 */
export function decideApplication(
  id: string,
  decision: ApprovalDecision,
): Promise<CoachApplicationDetail> {
  return request<CoachApplicationDetail>(endpoints.trainers.approval(id), {
    method: 'PATCH',
    body: decision,
  });
}

/**
 * `DELETE /admin/trainers/:id` - permanent, and the only irreversible action in
 * this drawer.
 *
 * ⚠ Not the same thing as rejecting. A rejection leaves the application in
 * place, tells the coach why, and can be looked at again. This erases the card,
 * every certificate, transformation and media item submitted with it, and the
 * moderation-inbox rows that point at them - then closes the account behind it
 * so the same person cannot immediately resubmit what was just removed.
 *
 * SUPER_ADMIN only, and the server enforces both that and the typed
 * confirmation - the dialog in front of this is friction, not the control.
 */
export function deleteApplication(
  id: string,
  input: { confirm: 'DELETE APPLICATION'; reason: string },
): Promise<{ uid: string; deletedAt: string }> {
  return request<{ uid: string; deletedAt: string }>(endpoints.trainers.remove(id), {
    method: 'DELETE',
    body: input,
  });
}
